import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Tab, Tabs } from '@mui/material';
import { ArrowLeft, CalendarDays, Mail, Pencil, UserX } from 'lucide-react';
import {
  useGetEmployeeByIdQuery,
  useGetEmployeeTimeOffSummaryQuery,
  useGetEmployeesQuery,
} from '../store/slices/employeeSlice';
import {
  useGenerateEmployeeEmailQuery,
  useUpdateTimeOffEntryMutation,
  useDeleteTimeOffEntryMutation,
  useGetFilteredTimeOffEntriesQuery,
} from '../store/slices/timeOffSlice';
import { useDispatch } from 'react-redux';
import { openModal } from '../store/slices/uiSlice';
import { TimeOffEntry } from '../types/timeOff';
import {
  aggregateTimeOffEntries,
  calculateAllocationPace,
  calculateWfhWeeklyPlan,
  combineAllocationPaces,
  getEmployeeElapsedFraction,
  getYearProgress,
  normalizeWeeklyWfhAllowance,
} from '../utils/timeOffUsage';
import {
  buildUncountedTimePeerComparison,
  summarizeUncountedTime,
} from '../utils/uncountedTime';
import { useEasternToday } from '../hooks/useEasternToday';
import { useAuth } from '../contexts/AuthContext';
import { isManagerLike, isOwnRequestLocked } from '../utils/roles';
import {
  ConfirmDialog,
  CountBadge,
  EmptyState,
  ErrorState,
  PageHeader,
  PersonAvatar,
  SectionCard,
  StatusPill,
} from '../components/ui';
import { PageError } from '../components/common/PageState';
import { sbg } from '../theme/sbgTokens';
import { formatCount, formatDateRange, formatTime } from '../utils/format';
import BalancesCard from './employee-detail/BalancesCard';
import {
  DetailsCard,
  OverrideRequestsCard,
  PtoNoticeCard,
  UpcomingCard,
  WfhCard,
} from './employee-detail/OverviewCards';
import type { NextWfhDay, PtoNoticeStats } from './employee-detail/OverviewCards';
import HistoryTable from './employee-detail/HistoryTable';
import UncountedTime from './employee-detail/UncountedTime';
import EditEntryDialog from './employee-detail/EditEntryDialog';
import RecordSkeleton from './employee-detail/RecordSkeleton';
import { EMPLOYMENT_STATUS_LABELS, formatStartLine, typeLabel } from './employee-detail/meta';
import { displayRole, explainRequestError, isGenericRole, resolvePersonName } from './employee-detail/text';

type RecordTab = 'overview' | 'history' | 'uncounted';

const WFH_ESCALATION_REASONS = [
  'wfh_not_approved',
  'wfh_escalated_to_vp',
  'wfh_vp_approved',
  'wfh_vp_approved_with_leads',
  'wfh_vp_declined',
  'wfh_hr_approved',
  'wfh_hr_declined',
  'wfh_rescinded',
];

const PTO_SHORT_NOTICE_REASONS = [
  'pto_lead_time',
  'pto_manager_approved_short_notice',
  'pto_manager_declined',
];

/** "What happened + why + what to do": a 400 shows the server's reason so the dates can be fixed. */
const saveErrorMessage = (error: unknown) =>
  `Couldn't save the entry. ${explainRequestError(error, {
    retry: 'Try again; your changes are still here.',
    fix: 'Fix the details and save again.',
  })}`;
const deleteErrorMessage = (error: unknown) => `Couldn't delete the entry. ${explainRequestError(error)}`;

const entryIdOf = (entry: TimeOffEntry) => entry._id || (entry as any).id;

const TabLabel: React.FC<{ text: string; count?: number; unit: [string, string] }> = ({ text, count, unit }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {text}
    {typeof count === 'number' && <CountBadge count={count} aria-label={formatCount(count, unit[0], unit[1])} />}
  </Box>
);

const EmployeeDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { profile, user } = useAuth();
  const role = profile?.role;
  const canEditEmployee = role === 'admin' || role === 'hr';
  const canDeleteTimeOff = canEditEmployee;
  const isJrAeView = role === 'jr_ae';

  const [tab, setTab] = useState<RecordTab>('overview');
  // A control that switches tabs from inside a panel unmounts with that panel; move focus to the new tab.
  const pendingTabFocus = useRef<RecordTab | null>(null);
  useEffect(() => {
    if (pendingTabFocus.current === tab) {
      pendingTabFocus.current = null;
      document.getElementById(`employee-tab-${tab}`)?.focus();
    }
  }, [tab]);
  const goToTab = (next: RecordTab) => {
    pendingTabFocus.current = next;
    setTab(next);
  };
  const [emailGenerated, setEmailGenerated] = useState(false);
  const [emailTimeoutError, setEmailTimeoutError] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeOffEntry | null>(null);
  const [editingOriginal, setEditingOriginal] = useState<TimeOffEntry | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [updateTimeOffEntry, { isLoading: isUpdating }] = useUpdateTimeOffEntryMutation();
  const [deleteTimeOffEntry, { isLoading: isDeleting }] = useDeleteTimeOffEntryMutation();

  const {
    data: employee,
    isLoading: isLoadingEmployee,
    error: employeeError,
    refetch: refetchEmployee,
  } = useGetEmployeeByIdQuery(id);
  // Managers edit their team's time off, never their own record's: their
  // manager or HR changes that, as the API enforces.
  const canEditTimeOff = canEditEmployee
    || (isManagerLike(role) && !isOwnRequestLocked(employee, { role, userId: user?.id }));
  const {
    data: peerEmployees = [],
    isLoading: isLoadingPeerEmployees,
    isError: hasPeerEmployeeError,
    refetch: refetchPeerEmployees,
  } = useGetEmployeesQuery();

  const {
    data: timeOffSummary,
    isLoading: isLoadingSummary,
    error: summaryError,
    refetch: refetchSummary,
    fulfilledTimeStamp: summaryUpdatedAt,
  } = useGetEmployeeTimeOffSummaryQuery(id);

  const {
    data: emailData,
    isLoading: isLoadingEmail,
    isFetching: isFetchingEmail,
    error: emailError,
    refetch: refetchEmail,
  } = useGenerateEmployeeEmailQuery(id, { skip: !emailGenerated });

  useEffect(() => {
    if (emailGenerated) {
      const timeoutId = setTimeout(() => {
        setEmailTimeoutError(true);
      }, 15000);

      refetchEmail()
        .catch(err => console.error('Refetch error:', err))
        .finally(() => clearTimeout(timeoutId));

      return () => clearTimeout(timeoutId);
    }
  }, [emailGenerated, refetchEmail]);

  useEffect(() => {
    if (emailData && emailGenerated) {
      handlePreviewEmail();
    }
  }, [emailData]);

  const todayYmd = useEasternToday();
  const yearProgress = useMemo(() => getYearProgress(todayYmd), [todayYmd]);
  const currentYear = yearProgress.year;
  const {
    data: peerTimeOffEntries = [],
    isLoading: isLoadingUncountedPeers,
    isError: hasUncountedPeerError,
    refetch: refetchPeerTimeOff,
  } = useGetFilteredTimeOffEntriesQuery({
    startDate: `${currentYear}-01-01`,
    startBefore: `${currentYear}-12-31`,
  });
  const summaryEntries = useMemo<TimeOffEntry[]>(
    () => timeOffSummary?.recentEntries || timeOffSummary?.entries || [],
    [timeOffSummary]
  );
  const usageData = useMemo(
    () => aggregateTimeOffEntries(summaryEntries, currentYear, todayYmd),
    [summaryEntries, currentYear, todayYmd]
  );
  const employeeElapsedFraction = useMemo(
    () => getEmployeeElapsedFraction(
      currentYear,
      todayYmd,
      employee?.customAllotment ? undefined : employee?.startDate
    ),
    [currentYear, todayYmd, employee?.customAllotment, employee?.startDate]
  );
  const ptoPace = useMemo(
    () => calculateAllocationPace(usageData.pto, employee?.ptoAllotment || 0, employeeElapsedFraction),
    [usageData.pto, employee?.ptoAllotment, employeeElapsedFraction]
  );
  const sickPace = useMemo(
    () => calculateAllocationPace(usageData.sick, employee?.sickTimeAllotment || 0, employeeElapsedFraction),
    [usageData.sick, employee?.sickTimeAllotment, employeeElapsedFraction]
  );
  const combinedPace = useMemo(
    () => combineAllocationPaces(ptoPace, sickPace),
    [ptoPace, sickPace]
  );
  const weeklyWfhAllowance = normalizeWeeklyWfhAllowance(
    employee?.wfhEligible === true,
    employee?.wfhAllotment || 0
  );
  const wfhWeeklyPlan = useMemo(
    () => calculateWfhWeeklyPlan(summaryEntries, currentYear, weeklyWfhAllowance),
    [summaryEntries, currentYear, weeklyWfhAllowance]
  );
  const wfhDisplay = {
    used: usageData.wfh.used / 8,
    scheduled: usageData.wfh.scheduled / 8,
    pending: usageData.wfh.pending / 8,
  };
  const uncountedTime = useMemo(
    () => summarizeUncountedTime(summaryEntries, currentYear),
    [summaryEntries, currentYear]
  );
  const uncountedPeerComparison = useMemo(
    () => buildUncountedTimePeerComparison(
      peerTimeOffEntries,
      peerEmployees,
      employee?._id || id,
      currentYear
    ),
    [peerTimeOffEntries, peerEmployees, employee?._id, id, currentYear]
  );
  const isLoadingUncountedComparison = isLoadingUncountedPeers || isLoadingPeerEmployees;
  const hasUncountedComparisonError = hasUncountedPeerError || hasPeerEmployeeError;

  const wfhEscalations = useMemo(
    () => summaryEntries.filter(
      (e: any) => e.type === 'WFH' && e.reviewReason && WFH_ESCALATION_REASONS.includes(e.reviewReason)
    ),
    [summaryEntries]
  );

  const ptoNotice = useMemo<PtoNoticeStats | null>(() => {
    const ptoEntries = summaryEntries.filter((e: any) => e.type === 'PTO' && e.status !== 'REJECTED');
    if (ptoEntries.length === 0) return null;
    const shortNotice = ptoEntries.filter((e: any) => PTO_SHORT_NOTICE_REASONS.includes(e.reviewReason)).length;
    const compliant = ptoEntries.length - shortNotice;
    return {
      total: ptoEntries.length,
      compliant,
      shortNotice,
      rate: Math.round((compliant / ptoEntries.length) * 100),
    };
  }, [summaryEntries]);

  // Presentational only: what's booked from today on, soonest first. Recurring WFH is stored one row
  // per day, so it would crowd PTO and sick time out of the card; the WFH card shows the next WFH day.
  const bookedFromToday = useMemo(
    () => summaryEntries
      .filter((entry) => entry.status !== 'REJECTED' && (entry.endDate || entry.startDate || '').slice(0, 10) >= todayYmd)
      .sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [summaryEntries, todayYmd]
  );
  const upcomingEntries = useMemo(
    () => bookedFromToday.filter((entry) => entry.type !== 'WFH'),
    [bookedFromToday]
  );
  const nextWfhDay = useMemo<NextWfhDay | null>(() => {
    const next = bookedFromToday.find((entry) => entry.type === 'WFH');
    if (!next) return null;
    const startYmd = (next.startDate || '').slice(0, 10);
    return { ymd: startYmd > todayYmd ? startYmd : todayYmd, pending: next.status === 'PENDING' };
  }, [bookedFromToday, todayYmd]);

  const entryPendingDelete = useMemo(
    () => (entryToDelete ? summaryEntries.find((entry) => entryIdOf(entry) === entryToDelete) ?? null : null),
    [entryToDelete, summaryEntries]
  );

  const backPath = isManagerLike(role) ? '/dashboard' : '/employees';
  const backLabel = isManagerLike(role) ? 'Back to dashboard' : 'Back to employees';

  const handleEdit = () => {
    if (employee) {
      dispatch(openModal({ type: 'employee-form', data: { employee, isEdit: true } }));
    }
  };

  const handleGenerateEmail = () => {
    if (emailGenerated) {
      setEmailGenerated(false);
      setTimeout(() => setEmailGenerated(true), 100);
    } else {
      setEmailGenerated(true);
    }
  };

  /**
   * Shows the drafted email to copy. There's no Send: GET
   * /time-off/employee/:id/email only drafts the text, and nothing in the API
   * sends a draft. (The Email activity page's single-employee send is a
   * different, server-built monthly summary, not this text.)
   */
  const handlePreviewEmail = () => {
    if (emailData && emailData.email) {
      const { to, subject, content } = emailData.email;
      dispatch(openModal({
        type: 'email-preview',
        data: { to, subject, content },
      }));
    }
  };

  const handleEditEntry = (entry: TimeOffEntry) => {
    const entryId = entry._id || (entry as any).id;
    setSaveError(null);
    setEditingOriginal(entry);
    setEditingEntry({ ...entry, _id: entryId } as TimeOffEntry);
  };

  const handleCloseEdit = () => {
    setEditingEntry(null);
    setEditingOriginal(null);
    setSaveError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    const entryId = editingEntry._id || (editingEntry as any).id;
    if (!entryId) {
      console.error('Missing time off entry id for update');
      return;
    }
    setSaveError(null);
    try {
      await updateTimeOffEntry({
        id: entryId,
        timeOff: {
          startDate: editingEntry.startDate,
          endDate: editingEntry.endDate,
          type: editingEntry.type,
          hours: editingEntry.hours,
          notes: editingEntry.notes,
          status: editingEntry.status,
          category: editingEntry.category,
          reason: editingEntry.reason
        }
      }).unwrap();
      await Promise.all([refetchSummary(), refetchEmployee()]);
      setEditingEntry(null);
      setEditingOriginal(null);
    } catch (error) {
      console.error('Failed to update time off entry:', error);
      setSaveError(saveErrorMessage(error));
    }
  };

  const handleDeleteConfirm = (entryId: string) => {
    if (!entryId) {
      console.error('No entry ID provided for delete');
      return;
    }
    setDeleteError(null);
    setEntryToDelete(entryId);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    setDeleteError(null);
    try {
      await deleteTimeOffEntry(entryToDelete).unwrap();
      setDeleteConfirmOpen(false);
      setEntryToDelete(null);
      await Promise.all([refetchSummary(), refetchEmployee()]);
    } catch (error) {
      console.error('Failed to delete time off entry:', error);
      setDeleteError(deleteErrorMessage(error));
    }
  };

  const backLink = (
    <Button
      component={RouterLink}
      to={backPath}
      variant="text"
      size="small"
      startIcon={<ArrowLeft size={16} />}
      aria-label={backLabel}
      sx={{ ml: -1.5, mb: 1.5, color: sbg.text.secondary, '&:hover': { color: sbg.text.primary } }}
    >
      {isManagerLike(role) ? 'Dashboard' : 'Employees'}
    </Button>
  );

  if (isLoadingEmployee) {
    return (
      <Box>
        {backLink}
        <RecordSkeleton label="Loading employee" />
      </Box>
    );
  }

  if (employeeError || !employee) {
    const status = (employeeError as { status?: unknown } | undefined)?.status;
    return (
      <Box>
        {backLink}
        {status === 404 ? (
          <SectionCard sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
            <EmptyState
              icon={<UserX />}
              title="Employee not found"
              body="This record may have been removed. The employee list has every current record."
              action={
                <Button component={RouterLink} to={backPath} variant="outlined">
                  {isManagerLike(role) ? 'Go to dashboard' : 'Go to employees'}
                </Button>
              }
            />
          </SectionCard>
        ) : status === 403 ? (
          <SectionCard sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
            <EmptyState
              title="You don't have access to this employee"
              body="Managers can view the employees assigned to them. Ask HR or an admin to update the assignment."
            />
          </SectionCard>
        ) : (
          <PageError
            title="Couldn't load this employee"
            description={explainRequestError(employeeError)}
            onRetry={() => { refetchEmployee(); }}
          />
        )}
      </Box>
    );
  }

  const summaryLoading = isLoadingSummary;
  const summaryFailed = !summaryLoading && Boolean(summaryError) && !timeOffSummary;
  const retrySummary = () => { refetchSummary(); };
  const firstName = employee.name.split(' ')[0] || employee.name;
  const isInactive = Boolean(employee.employmentStatus && employee.employmentStatus !== 'active');
  const emailBusy = isLoadingEmail || (emailGenerated && isFetchingEmail);
  const showEmailError = emailGenerated && Boolean(emailError) && !emailBusy && !emailTimeoutError;
  const updatedLabel = summaryUpdatedAt ? `Updated ${formatTime(summaryUpdatedAt)}` : null;
  const summaryErrorBody = explainRequestError(summaryError);
  const managerName = resolvePersonName(employee.managerName, peerEmployees);
  const vpName = resolvePersonName(employee.vpName, peerEmployees);

  // "Employee" is everyone's role, so it only adds noise to the header line.
  const meta = [
    isGenericRole(employee.role) ? null : displayRole(employee.role),
    employee.department?.name || 'No department',
    managerName ? `Reports to ${managerName}` : null,
    formatStartLine(employee.startDate, todayYmd),
  ];

  // WFH is an exception for in-office staff, so their WFH card follows the record details.
  // Placement depends only on the employee record, so it doesn't move when time off loads.
  const wfhAfterDetails = employee.wfhEligible !== true;
  const wfhCard = (
    <WfhCard
      year={currentYear}
      todayYmd={todayYmd}
      eligible={employee.wfhEligible === true}
      weeklyAllowance={weeklyWfhAllowance}
      usedDays={wfhDisplay.used}
      scheduledDays={wfhDisplay.scheduled}
      pendingDays={wfhDisplay.pending}
      plan={wfhWeeklyPlan}
      nextDay={nextWfhDay}
      loading={summaryLoading}
    />
  );

  const tabProps = (value: RecordTab) => ({
    value,
    id: `employee-tab-${value}`,
    'aria-controls': `employee-tabpanel-${value}`,
  });
  const panelProps = (value: RecordTab) => ({
    role: 'tabpanel',
    id: `employee-tabpanel-${value}`,
    'aria-labelledby': `employee-tab-${value}`,
    hidden: tab !== value,
  });

  return (
    <Box>
      {backLink}

      <PageHeader
        leading={<PersonAvatar name={employee.name} size={56} />}
        overline="Employee"
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {employee.name}
            {isInactive && (
              <StatusPill tone="neutral">
                {EMPLOYMENT_STATUS_LABELS[employee.employmentStatus] ?? 'Inactive'}
              </StatusPill>
            )}
          </Box>
        }
        meta={meta}
        actions={
          <>
            <Button
              component={RouterLink}
              to={`/calendar?employeeId=${employee._id}`}
              variant="text"
              startIcon={<CalendarDays size={16} />}
            >
              View calendar
            </Button>
            <Button
              variant="outlined"
              onClick={handleGenerateEmail}
              disabled={emailBusy}
              aria-busy={emailBusy || undefined}
              startIcon={emailBusy ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : <Mail size={16} />}
            >
              {emailBusy ? 'Drafting email…' : 'Draft time-off email'}
            </Button>
            {canEditEmployee && (
              <Button variant="outlined" onClick={handleEdit} startIcon={<Pencil size={16} />}>
                Edit employee
              </Button>
            )}
          </>
        }
      >
        <Tabs value={tab} onChange={(_, next: RecordTab) => setTab(next)} aria-label="Employee record" variant="scrollable" scrollButtons={false}>
          <Tab {...tabProps('overview')} label="Overview" />
          <Tab
            {...tabProps('history')}
            label={<TabLabel text="History" count={summaryLoading || summaryFailed ? undefined : summaryEntries.length} unit={['entry', 'entries']} />}
          />
          <Tab
            {...tabProps('uncounted')}
            label={<TabLabel text="Uncounted time" count={summaryLoading || summaryFailed ? undefined : uncountedTime.occurrences} unit={['entry', 'entries']} />}
          />
        </Tabs>
      </PageHeader>

      {(emailTimeoutError || showEmailError) && (
        <Box sx={{ display: 'grid', gap: 1.5, mb: 3 }}>
          {emailTimeoutError && (
            <Alert severity="warning" onClose={() => setEmailTimeoutError(false)}>
              The email draft is taking longer than usual. Try again in a few minutes.
            </Alert>
          )}
          {showEmailError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleGenerateEmail}>
                  Try again
                </Button>
              }
            >
              Couldn't draft the time-off email. The email service didn't respond.
            </Alert>
          )}
        </Box>
      )}

      {/* Overview */}
      <Box {...panelProps('overview')}>
        {tab === 'overview' && (
          summaryFailed ? (
            <SectionCard>
              <ErrorState
                title="Couldn't load time off"
                body={`Balances and history for ${firstName} didn't load. ${summaryErrorBody}`}
                onRetry={retrySummary}
              />
            </SectionCard>
          ) : (
            <Box sx={{ display: 'grid', gap: 3 }}>
              <BalancesCard
                year={currentYear}
                todayYmd={todayYmd}
                dayOfYear={yearProgress.dayOfYear}
                daysInYear={yearProgress.daysInYear}
                percentComplete={yearProgress.percentComplete}
                updatedLabel={updatedLabel}
                pto={ptoPace}
                sick={sickPace}
                combined={combinedPace}
                loading={summaryLoading}
              />
              {/* Main: WFH and the record. Rail: what's coming up and PTO notice. Late-arriving
                  override requests go last in the main column so nothing above them shifts. */}
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  alignItems: 'start',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' },
                }}
              >
                <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
                  {!wfhAfterDetails && wfhCard}
                  <DetailsCard
                    employee={employee}
                    weeklyWfhAllowance={weeklyWfhAllowance}
                    managerName={managerName}
                    vpName={vpName}
                  />
                  {wfhAfterDetails && wfhCard}
                  {!summaryLoading && wfhEscalations.length > 0 && <OverrideRequestsCard entries={wfhEscalations} />}
                </Box>
                <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
                  <UpcomingCard
                    entries={upcomingEntries}
                    loading={summaryLoading}
                    firstName={firstName}
                    onViewHistory={() => goToTab('history')}
                  />
                  <PtoNoticeCard stats={ptoNotice} loading={summaryLoading} />
                </Box>
              </Box>
            </Box>
          )
        )}
      </Box>

      {/* History: kept mounted so filters survive a tab switch */}
      <Box {...panelProps('history')}>
        <HistoryTable
          entries={summaryEntries}
          firstName={firstName}
          canEdit={canEditTimeOff}
          canDelete={canDeleteTimeOff}
          loading={summaryLoading}
          error={summaryFailed}
          errorBody={summaryErrorBody}
          onRetry={retrySummary}
          onEdit={handleEditEntry}
          onDelete={handleDeleteConfirm}
        />
      </Box>

      {/* Uncounted PTO / sick time */}
      <Box {...panelProps('uncounted')}>
        {tab === 'uncounted' && (
          <UncountedTime
            year={currentYear}
            summary={uncountedTime}
            comparison={uncountedPeerComparison}
            comparisonLoading={isLoadingUncountedComparison}
            comparisonError={hasUncountedComparisonError}
            onRetryComparison={() => {
              if (hasUncountedPeerError) refetchPeerTimeOff();
              if (hasPeerEmployeeError) refetchPeerEmployees();
            }}
            isJrAeView={isJrAeView}
            loading={summaryLoading}
            error={summaryFailed}
            errorBody={summaryErrorBody}
            onRetry={retrySummary}
          />
        )}
      </Box>

      <EditEntryDialog
        entry={editingEntry}
        original={editingOriginal}
        employeeName={employee.name}
        saving={isUpdating}
        error={saveError}
        onChange={setEditingEntry}
        onClose={handleCloseEdit}
        onSave={handleSaveEdit}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteError(null);
        }}
        onConfirm={handleDelete}
        title={
          entryPendingDelete
            ? `Delete ${typeLabel(entryPendingDelete.type)} on ${formatDateRange(entryPendingDelete.startDate, entryPendingDelete.endDate)}?`
            : 'Delete this time-off entry?'
        }
        body={`It's removed from ${employee.name}'s history and balances. This can't be undone.`}
        confirmLabel="Delete entry"
        loadingLabel="Deleting…"
        destructive
        loading={isDeleting}
      >
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </ConfirmDialog>
    </Box>
  );
};

export default EmployeeDetail;
